//—— 緋紅的三條故事線 ——
//
//  問診收六種處境，但故事只有三條線（Kevin 2026-10-19 定）：
//
//    單身線      單身中 ／ 剛結束一段 ／ 有在意的人
//    尚未交往線  曖昧還沒交往
//    已交往線    正在交往 ／ 已結婚
//
//  為什麼是三條不是六條：六種處境讀的是**同一件事的不同階段**，
//  但「有沒有第二張盤」與「問的是什麼」只有三種組合 ——
//  單身問「會來什麼人」、曖昧問「該不該開口」、在一起問「走得下去嗎」。
//  分六條等於要寫六份章節表，而其中四份會有一半內容重複。
//
//  —— 為什麼判斷要同時看 intent 與 status ——
//  問診有兩條路徑，各自問自己的關係狀態（見 SCENES 的 soloStatus / pairStatus），
//  兩邊都寫進 status 這個欄位。單看 status 分不出走的是哪一條 ——
//  例如「剛結束一段」只有單身路徑會出現，但那是巧合不是保證。
//  intent 才是分路的那一題，所以它是主判準，status 只用來把 pair 再切兩半。
//
//  —— ⚠️ 三條線是「結構」，六種處境是「語氣」，兩個都要用 ——
//
//  故事線只決定**版型與章節結構**：走哪一套故事頁區塊、幾張盤、章節表用哪一份。
//  但**寫報告的時候仍然要看原始的那六種 status**：
//  同樣在單身線，剛結束一段的人跟從沒談過的人要寫的東西差很多 ——
//  前者要處理的是還沒收拾好的情緒，後者是還沒開始的期待。
//  只看三條線的話，那六種人會拿到三份幾乎一樣的報告。
//
//  所以：storyLineOf() 給結構，STATUS[status] 給語氣，兩個一起用。
//
//  ⚠️ 這支是**唯一真相**。不要在別的地方自己寫 status === "..." 去判斷 ——
//  之後後端的報告引擎也要照這份鏡像一份（跨 client/ 與根目錄沒辦法共用模組），
//  鏡像的那一份要指回這裡。

export const STORY_LINE = {
  SINGLE: "single",
  AMBIGUOUS: "ambiguous",
  TOGETHER: "together",
};

//每一條線的樣子。報告與故事頁要用的東西都掛在這裡，
//  才不會變成「文案寫在 A 檔、章節寫在 B 檔、判斷寫在 C 檔」。
export const STORY_LINES = {
  [STORY_LINE.SINGLE]: {
    key: STORY_LINE.SINGLE,
    label: "單身",
    //  ⚠️ 只有一張盤：問診走 solo 那條路徑時不問對方的生辰。
    //  所以這條線的文案主詞是「你」不是「你們」，
    //  命書面板與章節卡都不能寫成合盤。
    pair: false,
    //  這條線在問什麼 —— 寫報告的 prompt 與故事頁的鉤子都照這個方向走
    asks: "會來什麼樣的人、什麼時候來、你自己擋在哪裡",
  },
  [STORY_LINE.AMBIGUOUS]: {
    key: STORY_LINE.AMBIGUOUS,
    label: "尚未交往",
    pair: true,
    asks: "該不該開口、什麼時候開口、他心裡有沒有你",
  },
  [STORY_LINE.TOGETHER]: {
    key: STORY_LINE.TOGETHER,
    label: "已交往",
    pair: true,
    asks: "走得下去嗎、卡在哪裡、能走到哪裡",
  },
};

//問診的答案 → 走哪一條故事線。
//
//  吃整份 intake（不是兩個參數）—— 呼叫端拿到的本來就是那包東西，
//  拆成參數只會讓每個呼叫點各自決定要傳什麼。
//
//  判不出來時回單身線：那是唯一「只需要一張盤」的線，
//  資料不全時退到它才不會去讀不存在的對方生辰。
export const storyLineOf = (intake) => {
  if (intake?.intent !== "pair") return STORY_LINE.SINGLE;
  return intake?.status === "ambiguous"
    ? STORY_LINE.AMBIGUOUS
    : STORY_LINE.TOGETHER;
};

//這條線要不要第二張盤。故事頁與報告都常常只關心這件事。
export const isPairLine = (intake) =>
  STORY_LINES[storyLineOf(intake)].pair;

//—— 六種處境 ——
//
//  故事線只有三條，但**報告要照這六種寫**（見上方那段 ⚠️）。
//  line 是它歸在哪一條線，nuance 是「這一種人的報告要特別處理什麼」——
//  那句話會進 prompt，所以寫的是方向不是形容詞。
//
//  key 就是問診存進 status 的值（見 SCENES 的 soloStatus / pairStatus）。
export const STATUS = {
  single: {
    label: "單身中",
    line: STORY_LINE.SINGLE,
    nuance: "還沒有對象，重點放在「會來什麼樣的人」與「你自己擋在哪裡」",
  },
  breakup: {
    label: "剛結束一段",
    line: STORY_LINE.SINGLE,
    //  ⚠️ 這一種最需要分開處理：情緒還沒收拾好的人，
    //  讀到「你會吸引什麼人」會覺得被跳過。要先接住上一段，再往前看。
    nuance: "上一段還沒放下，要先回頭談那一段的結構，再談下一個",
  },
  crush: {
    label: "有在意的人",
    line: STORY_LINE.SINGLE,
    //  ⚠️ 他有在意的人，但**我們沒有那個人的生辰**（走的是單身路徑）。
    //  所以只能從他自己的盤談「你會被什麼樣的人吸引」，
    //  不能寫成合盤，也不要假裝看得到對方。
    nuance: "有對象但沒有對方的八字，只能從他自己的盤談吸引力與盲點",
  },
  ambiguous: {
    label: "曖昧還沒交往",
    line: STORY_LINE.AMBIGUOUS,
    nuance: "兩張盤都有，但關係還沒定下來，重點是時機與該不該主動",
  },
  dating: {
    label: "正在交往",
    line: STORY_LINE.TOGETHER,
    nuance: "進行中的關係，重點是衝突的結構與走勢",
  },
  married: {
    label: "已結婚",
    line: STORY_LINE.TOGETHER,
    //  已婚跟交往中讀的是同一組素材，但不能用「要不要繼續」那種問法 ——
    //  他問的是怎麼走下去，不是走不走。
    nuance: "已成家，不談去留只談如何經營，忌諱寫成勸合或勸分",
  },
};

//取這一份問診的處境設定。查不到就回單身中（同 storyLineOf 的退路）。
export const statusOf = (intake) => STATUS[intake?.status] || STATUS.single;
